import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Printer,
  Layers,
  FileSpreadsheet,
  ArrowRight,
  ArrowDown,
  Building2,
  Calendar,
  User,
  ShieldCheck,
  CheckCircle2,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useQuery } from '@tanstack/react-query';
import { formatNumber } from '@/lib/utils';

interface PalletJobTagPrintModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  materialId?: string;
  productionOrderId?: string;
}

export function PalletJobTagPrintModal({
  open,
  onOpenChange,
  materialId,
  productionOrderId,
}: PalletJobTagPrintModalProps) {
  const [selectedPalletIndex, setSelectedPalletIndex] = useState<number | 'ALL'>('ALL');
  const printRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery(
    trpc.productionPlanning.getPalletTagDetails.queryOptions(
      {
        materialId: materialId || undefined,
        productionOrderId: productionOrderId || undefined,
      },
      {
        enabled: open && !!(materialId || productionOrderId),
      }
    )
  );

  const tags = data?.tags || [];
  const displayTags = selectedPalletIndex === 'ALL'
    ? tags
    : tags.filter((t: any) => t.palletIndex === selectedPalletIndex);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank', 'width=1100,height=850');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>THE_PALLET_${data?.poCode || 'GIAY_OFFSET'}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 8mm;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              margin: 0;
              padding: 0;
              color: #0f172a;
              background: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .pallet-page {
              page-break-after: always;
              height: 98vh;
              display: flex;
              flex-direction: column;
              border: 3px solid #0f172a;
              border-radius: 8px;
              box-sizing: border-box;
              padding: 16px 20px;
              margin-bottom: 20px;
            }
            .pallet-page:last-child {
              page-break-after: avoid;
              margin-bottom: 0;
            }
            .header-table {
              width: 100%;
              border-collapse: collapse;
              border-bottom: 2px solid #0f172a;
              padding-bottom: 8px;
            }
            .hero-box {
              display: flex;
              justify-content: space-between;
              align-items: center;
              background: #f8fafc;
              border: 2px solid #0f172a;
              border-radius: 6px;
              padding: 12px 18px;
              margin: 12px 0;
            }
            .po-title {
              font-size: 34px;
              font-weight: 900;
              font-family: monospace;
              letter-spacing: -1px;
              color: #0f172a;
            }
            .pallet-indicator {
              font-size: 36px;
              font-weight: 900;
              color: #1e3a8a;
              background: #dbeafe;
              border: 2px solid #1d4ed8;
              padding: 6px 18px;
              border-radius: 6px;
            }
            .specs-grid {
              width: 100%;
              border-collapse: collapse;
              margin: 10px 0;
              flex: 1;
            }
            .specs-grid td, .specs-grid th {
              border: 1.5px solid #cbd5e1;
              padding: 10px 14px;
              vertical-align: middle;
            }
            .specs-grid th {
              background: #f1f5f9;
              font-size: 13px;
              font-weight: 700;
              color: #334155;
              text-align: left;
              width: 22%;
            }
            .specs-grid td {
              font-size: 16px;
              color: #0f172a;
            }
            .grain-badge {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              font-weight: 800;
              font-size: 15px;
              padding: 4px 12px;
              border-radius: 4px;
              background: #fef3c7;
              color: #92400e;
              border: 1px solid #d97706;
            }
            .footer-signatures {
              width: 100%;
              border-collapse: collapse;
              margin-top: auto;
              border-top: 1.5px solid #0f172a;
              padding-top: 8px;
            }
            .sig-col {
              text-align: center;
              width: 33.33%;
              padding: 8px;
            }
            .sig-title {
              font-size: 13px;
              font-weight: 700;
              text-transform: uppercase;
              color: #334155;
              margin-bottom: 45px;
            }
            .sig-name {
              font-size: 13px;
              font-weight: 600;
              color: #0f172a;
            }
            @media print {
              .no-print { display: none !important; }
              body { margin: 0; padding: 0; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between pr-6">
            <div className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-indigo-600" />
              <DialogTitle>
                In Thẻ Pallet Nhận Diện Giấy In Offset 
              </DialogTitle>
            </div>
            {data && (
              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 font-mono font-bold">
                {data.palletCount} Pallet ({formatNumber(data.totalSheets)} tờ)
              </Badge>
            )}
          </div>
          <DialogDescription className="text-xs text-slate-500">
            Dán thẻ nhận diện vào 4 mặt kiện pallet sau khi xả mộc. Thợ máy in quét mã QR để nhận diện phôi và đối chiếu xớ giấy trước khi gá lên bàn nạp giấy Feeder.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-12 text-center text-slate-500 flex flex-col items-center gap-2">
            <div className="animate-spin h-6 w-6 border-2 border-indigo-600 border-t-transparent rounded-full" />
            <span className="text-xs">Đang chuẩn bị hồ sơ Pallet...</span>
          </div>
        ) : !data || tags.length === 0 ? (
          <div className="py-8 text-center text-slate-500">
            Không tìm thấy thông tin kiện pallet giấy của Lệnh sản xuất này.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Filter Pallet Option */}
            <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-semibold text-slate-700">Chọn Thẻ Cần In:</Label>
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    variant={selectedPalletIndex === 'ALL' ? 'default' : 'outline'}
                    size="sm"
                    className="h-8 text-xs font-semibold"
                    onClick={() => setSelectedPalletIndex('ALL')}
                  >
                    Tất cả ({tags.length})
                  </Button>
                  {tags.map((t: any) => (
                    <Button
                      key={t.palletIndex}
                      type="button"
                      variant={selectedPalletIndex === t.palletIndex ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 text-xs font-mono"
                      onClick={() => setSelectedPalletIndex(t.palletIndex)}
                    >
                      P{t.palletIndex} ({formatNumber(t.palletSheets)} tờ)
                    </Button>
                  ))}
                </div>
              </div>

              <div className="text-xs text-slate-500">
                Khổ giấy in khuyến nghị: <strong>A4 Landscape (Ngang)</strong>
              </div>
            </div>

            {/* Printable Preview Area */}
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-100/60 max-h-[500px] overflow-y-auto">
              <div ref={printRef}>
                {displayTags.map((tag: any) => (
                  <div
                    key={tag.palletIndex}
                    className="pallet-page bg-white p-6 rounded-lg border-2 border-slate-900 mb-6 shadow-sm"
                  >
                    {/* Header */}
                    <table className="w-full border-b-2 border-slate-900 pb-2">
                      <tbody>
                        <tr>
                          <td style={{ width: '65%' }}>
                            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                              CÔNG TY CỔ PHẦN BAO BÌ APV &bull; HỆ THỐNG QUẢN TRỊ SẢN XUẤT MES
                            </div>
                            <div className="text-2xl font-black text-slate-900 mt-0.5">
                              THẺ NHẬN DIỆN PALLET PHÔI GIẤY IN OFFSET
                            </div>
                          </td>
                          <td style={{ width: '35%', textAlign: 'right' }}>
                            <div className="inline-block border-2 border-slate-900 bg-slate-50 p-2 rounded">
                              <QRCodeSVG value={tag.qrPayload} size={84} level="M" />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Hero Banner */}
                    <div className="flex items-center justify-between bg-slate-50 border-2 border-slate-900 rounded-lg p-3 my-3">
                      <div>
                        <div className="text-xs uppercase font-bold text-slate-500">Mã Lệnh Sản Xuất (PO CODE)</div>
                        <div className="text-3xl font-black font-mono text-slate-950 tracking-tight">
                          {tag.poCode}
                        </div>
                        {tag.poReference && (
                          <div className="text-xs text-slate-600 font-mono">PO Ref: {tag.poReference}</div>
                        )}
                      </div>

                      <div className="text-right">
                        <div className="text-xs uppercase font-bold text-indigo-700">Thứ Tự Kiện Hàng</div>
                        <div className="text-3xl font-black text-indigo-900 bg-indigo-50 border-2 border-indigo-600 px-4 py-1 rounded-md">
                          PALLET {tag.palletIndex} / {tag.totalPallets}
                        </div>
                      </div>
                    </div>

                    {/* Specs Table */}
                    <table className="w-full border-collapse border border-slate-300 text-sm my-2">
                      <tbody>
                        <tr>
                          <th className="bg-slate-100 p-2 text-left font-bold text-slate-700 border border-slate-300 w-1/4">
                            Khách Hàng
                          </th>
                          <td className="p-2 border border-slate-300 font-semibold text-slate-900">
                            {tag.customerName}
                          </td>
                          <th className="bg-slate-100 p-2 text-left font-bold text-slate-700 border border-slate-300 w-1/4">
                            Sản Lượng Kiện Này
                          </th>
                          <td className="p-2 border border-slate-300">
                            <span className="text-xl font-black font-mono text-emerald-700">
                              {formatNumber(tag.palletSheets)}
                            </span>{' '}
                            <span className="text-xs font-semibold text-slate-500">
                              / {formatNumber(tag.totalSheets)} tờ tổng LSX
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th className="bg-slate-100 p-2 text-left font-bold text-slate-700 border border-slate-300">
                            Tên Sản Phẩm / Bài In
                          </th>
                          <td className="p-2 border border-slate-300 font-medium text-slate-900" colSpan={3}>
                            <strong>{tag.productName}</strong> &bull; Mã bài: <span className="font-mono text-indigo-700">{tag.layoutCode}</span>
                          </td>
                        </tr>
                        <tr>
                          <th className="bg-slate-100 p-2 text-left font-bold text-slate-700 border border-slate-300">
                            Quy Cách Giấy
                          </th>
                          <td className="p-2 border border-slate-300 font-medium">
                            {tag.paperSpecs}
                          </td>
                          <th className="bg-slate-100 p-2 text-left font-bold text-slate-700 border border-slate-300">
                            Khổ Giấy Xả Mộc
                          </th>
                          <td className="p-2 border border-slate-300 font-mono font-bold text-base text-slate-900">
                            {tag.printingPaperSize}
                            <span className="text-xs text-slate-500 font-normal ml-2">
                              (Từ ram {tag.purchasedPaperSize}, chia {tag.cutRatio})
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th className="bg-slate-100 p-2 text-left font-bold text-slate-700 border border-slate-300">
                            Chiều Xớ Giấy (Grain)
                          </th>
                          <td className="p-2 border border-slate-300">
                            {tag.grainDirection === 'LONG_GRAIN' ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded bg-amber-50 text-amber-900 border border-amber-300 font-bold text-sm">
                                <ArrowDown className="h-4 w-4 text-amber-700" />
                                XỚ XUÔI (Long Grain &bull; Dọc nhíp in)
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded bg-purple-50 text-purple-900 border border-purple-300 font-bold text-sm">
                                <ArrowRight className="h-4 w-4 text-purple-700" />
                                XỚ NGANG (Short Grain &bull; Ngang nhíp)
                              </span>
                            )}
                          </td>
                          <th className="bg-slate-100 p-2 text-left font-bold text-slate-700 border border-slate-300">
                            Máy In Chỉ Định
                          </th>
                          <td className="p-2 border border-slate-300 font-bold text-slate-900">
                            {tag.targetMachine}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Signatures */}
                    <table className="w-full mt-4 pt-2 border-t border-slate-400 text-xs">
                      <tbody>
                        <tr>
                          <td className="w-1/3 text-center p-2">
                            <div className="font-bold uppercase text-slate-600 mb-8">THỢ CẮT XÉN PHÔI</div>
                            <div className="font-semibold text-slate-900">{tag.slittedBy}</div>
                          </td>
                          <td className="w-1/3 text-center p-2">
                            <div className="font-bold uppercase text-slate-600 mb-8">KCS KIỂM TRA PHÔI</div>
                            <div className="text-slate-400 italic">(Ký & ghi rõ họ tên)</div>
                          </td>
                          <td className="w-1/3 text-center p-2">
                            <div className="font-bold uppercase text-slate-600 mb-8">THỢ MÁY IN NHẬN GIẤY</div>
                            <div className="text-slate-400 italic">(Ký khi gá giấy lên Feeder)</div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Đóng
          </Button>
          <Button
            onClick={handlePrint}
            disabled={isLoading || !data || tags.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold gap-2 shadow-xs"
          >
            <Printer className="h-4 w-4" />
            In {displayTags.length} Thẻ Pallet (Khổ A4)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
